import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Plus, 
    Edit, 
    Trash2, 
    User, 
    Shield, 
    GraduationCap, 
    UserCheck, 
    Lock, 
    Unlock, 
    RotateCcw, 
    AlertTriangle,
    Users as UsersIcon,
    Archive,
    Clock,
    Briefcase,
    Search,
    MoreVertical,
    XCircle,
    CheckCircle,
    Loader2,
    Globe,
    Mic,
    Video,
    Building2,
    Settings2,
    BookOpen,
    DollarSign,
    Scale,
    ShieldCheck
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ATSIntegration from '../components/admin/ATSIntegration';

const Users = () => {
    const { api } = useAuth();
    const [users, setUsers] = useState([]);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    
    // Confirmation dialogs
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Feature access dialog
    const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
    const [featureEditUser, setFeatureEditUser] = useState(null);
    const [featureAccess, setFeatureAccess] = useState({
        simulations: true,
        job_simulations: true,
        sourcing_labs: true,
        evolve: true,
        staffing_training: true,
        inside_sales_training: true,
        communication_training: true,
        leadership_training: true,
        tech_learning: true,
        finance_training: true,
        eeoc_training: true,
        quality_admin: false
    });
    
    // State for dynamic course modules
    const [dynamicModules, setDynamicModules] = useState([]);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'user',
        designation: '',
        department: ''
    });

    // Memoized fetch functions to avoid stale closures
    const fetchUsers = useCallback(async () => {
        try {
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    }, [api]);

    const fetchDeletedUsers = useCallback(async () => {
        try {
            const response = await api.get('/users/deleted');
            setDeletedUsers(response.data);
        } catch (error) {
            console.error('Failed to load deleted users:', error);
        }
    }, [api]);

    const fetchDepartments = useCallback(async () => {
        try {
            const response = await api.get('/users/departments/list');
            setDepartments(response.data.departments || []);
        } catch (error) {
            console.error('Failed to load departments:', error);
        }
    }, [api]);

    // Fetch dynamic course modules
    const fetchDynamicModules = useCallback(async () => {
        try {
            const response = await api.get('/course-modules?include_inactive=true');
            setDynamicModules(response.data.modules || []);
        } catch (error) {
            console.error('Failed to load course modules:', error);
        }
    }, [api]);

    useEffect(() => {
        fetchUsers();
        fetchDeletedUsers();
        fetchDepartments();
        fetchDynamicModules();
    }, [fetchUsers, fetchDeletedUsers, fetchDepartments, fetchDynamicModules]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (editingUser) {
                const updates = { 
                    name: formData.name,
                    role: formData.role,
                    designation: formData.designation || null,
                    department: formData.department || null
                };
                if (formData.password) {
                    updates.password = formData.password;
                }
                await api.put(`/users/${editingUser.id}`, updates);
                toast.success('User updated successfully');
            } else {
                await api.post('/users/create', {
                    ...formData,
                    department: formData.department || null
                });
                toast.success('User created successfully');
            }
            fetchUsers();
            fetchDepartments();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSoftDelete = async () => {
        if (!selectedUserId) return;
        setActionLoading(true);
        try {
            await api.delete(`/users/${selectedUserId}`);
            toast.success('User deleted. Can be restored within 30 days.');
            fetchUsers();
            fetchDeletedUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        } finally {
            setActionLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUserId(null);
        }
    };

    const handlePermanentDelete = async () => {
        if (!selectedUserId) return;
        setActionLoading(true);
        try {
            await api.delete(`/users/${selectedUserId}/permanent`);
            toast.success('User permanently deleted');
            fetchUsers();
            fetchDeletedUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        } finally {
            setActionLoading(false);
            setPermanentDeleteDialogOpen(false);
            setSelectedUserId(null);
        }
    };

    const handleRestore = async (userId) => {
        setActionLoading(true);
        try {
            await api.post(`/users/${userId}/restore`);
            toast.success('User restored successfully');
            fetchUsers();
            fetchDeletedUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to restore user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleLockToggle = async (userId, isLocked) => {
        try {
            if (isLocked) {
                await api.post(`/users/${userId}/unlock`);
                toast.success('User unlocked');
            } else {
                await api.post(`/users/${userId}/lock`);
                toast.success('User locked');
            }
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update user status');
        }
    };

    const resetForm = () => {
        setFormData({ name: '', email: '', password: '', role: 'user', designation: '', department: '' });
        setEditingUser(null);
        setDialogOpen(false);
    };

    const openEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '',
            role: user.role,
            designation: user.designation || '',
            department: user.department || ''
        });
        setDialogOpen(true);
    };

    const openFeatureAccessDialog = (user) => {
        setFeatureEditUser(user);
        
        // Build feature access state including dynamic modules
        const baseAccess = {
            simulations: user.feature_access?.simulations !== false,
            job_simulations: user.feature_access?.job_simulations !== false,
            sourcing_labs: user.feature_access?.sourcing_labs !== false,
            evolve: user.feature_access?.evolve !== false,
            staffing_training: user.feature_access?.staffing_training !== false,
            inside_sales_training: user.feature_access?.inside_sales_training !== false,
            communication_training: user.feature_access?.communication_training !== false,
            leadership_training: user.feature_access?.leadership_training !== false,
            tech_learning: user.feature_access?.tech_learning !== false,
            finance_training: user.feature_access?.finance_training !== false,
            eeoc_training: user.feature_access?.eeoc_training !== false,
            quality_admin: user.feature_access?.quality_admin === true
        };
        
        // Add dynamic modules
        dynamicModules.forEach(module => {
            const featureKey = module.slug.replace(/-/g, '_');
            baseAccess[featureKey] = user.feature_access?.[featureKey] !== false;
        });
        
        setFeatureAccess(baseAccess);
        setFeatureDialogOpen(true);
    };

    const handleFeatureAccessUpdate = async () => {
        if (!featureEditUser) return;
        setActionLoading(true);
        try {
            await api.put(`/users/${featureEditUser.id}/feature-access`, featureAccess);
            toast.success('Feature access updated successfully');
            fetchUsers();
            setFeatureDialogOpen(false);
            setFeatureEditUser(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update feature access');
        } finally {
            setActionLoading(false);
        }
    };

    const openDeleteDialog = (userId) => {
        setSelectedUserId(userId);
        setDeleteDialogOpen(true);
    };

    const openPermanentDeleteDialog = (userId) => {
        setSelectedUserId(userId);
        setPermanentDeleteDialogOpen(true);
    };

    const getRoleIcon = (role) => {
        switch(role) {
            case 'admin': return <Shield className="h-4 w-4" />;
            case 'trainer': return <UserCheck className="h-4 w-4" />;
            default: return <GraduationCap className="h-4 w-4" />;
        }
    };

    const getRoleColor = (role) => {
        switch(role) {
            case 'admin': return 'bg-primary/10 text-primary';
            case 'trainer': return 'bg-secondary/10 text-secondary';
            default: return 'bg-muted text-muted-foreground';
        }
    };

    const getDaysUntilExpiry = (deletedAt) => {
        if (!deletedAt) return 30;
        const deletedDate = new Date(deletedAt);
        const now = new Date();
        const daysSince = Math.floor((now - deletedDate) / (1000 * 60 * 60 * 24));
        return Math.max(0, 30 - daysSince);
    };

    const filteredUsers = users.filter(user => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.name?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query) ||
            user.designation?.toLowerCase().includes(query) ||
            user.role?.toLowerCase().includes(query)
        );
    });

    const filteredDeletedUsers = deletedUsers.filter(user => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.name?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
        );
    });

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="users-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <UsersIcon className="h-8 w-8 text-primary" />
                            User Management
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Create, edit, and manage platform users
                        </p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="create-user-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add User
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
                                <DialogDescription>
                                    {editingUser 
                                        ? 'Update user details below. Leave password blank to keep current.'
                                        : 'Fill in the details to create a new user account.'}
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Enter full name"
                                        required
                                        data-testid="user-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="user@example.com"
                                        required
                                        disabled={!!editingUser}
                                        data-testid="user-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">
                                        Password {editingUser && <span className="text-muted-foreground">(leave blank to keep current)</span>}
                                    </Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder={editingUser ? '••••••••' : 'Enter password'}
                                        required={!editingUser}
                                        data-testid="user-password-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="designation">Designation</Label>
                                    <Input
                                        id="designation"
                                        value={formData.designation}
                                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                                        placeholder="e.g., Senior Recruiter, HR Manager"
                                        data-testid="user-designation-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="department">Department</Label>
                                    <Input
                                        id="department"
                                        value={formData.department}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                        placeholder="e.g., HR, Sales, Engineering"
                                        list="department-list"
                                        data-testid="user-department-input"
                                    />
                                    <datalist id="department-list">
                                        {departments.map(dept => (
                                            <option key={dept} value={dept} />
                                        ))}
                                    </datalist>
                                </div>
                                <div className="space-y-2">
                                    <Label>Role</Label>
                                    <Select
                                        value={formData.role}
                                        onValueChange={(value) => setFormData({ ...formData, role: value })}
                                    >
                                        <SelectTrigger data-testid="user-role-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="user">
                                                <div className="flex items-center gap-2">
                                                    <GraduationCap className="h-4 w-4" />
                                                    Trainee
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="trainer">
                                                <div className="flex items-center gap-2">
                                                    <UserCheck className="h-4 w-4" />
                                                    Trainer
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="admin">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="h-4 w-4" />
                                                    Admin
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <DialogFooter className="gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={resetForm}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={actionLoading} data-testid="save-user-btn">
                                        {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                        {editingUser ? 'Update User' : 'Create User'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <UsersIcon className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Users</p>
                                    <p className="text-2xl font-bold">{users.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-emerald-500/10">
                                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Active</p>
                                    <p className="text-2xl font-bold">{users.filter(u => !u.is_locked).length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-amber-500/10">
                                    <Lock className="h-5 w-5 text-amber-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Locked</p>
                                    <p className="text-2xl font-bold">{users.filter(u => u.is_locked).length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-red-500/10">
                                    <Archive className="h-5 w-5 text-red-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Deleted</p>
                                    <p className="text-2xl font-bold">{deletedUsers.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs and Search */}
                <div className="flex items-center justify-between gap-4">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
                        <TabsList>
                            <TabsTrigger value="active" className="gap-2" data-testid="tab-active-users">
                                <UsersIcon className="h-4 w-4" />
                                Active Users ({users.length})
                            </TabsTrigger>
                            <TabsTrigger value="deleted" className="gap-2" data-testid="tab-deleted-users">
                                <Archive className="h-4 w-4" />
                                Deleted ({deletedUsers.length})
                            </TabsTrigger>
                            <TabsTrigger value="ats" className="gap-2" data-testid="tab-ats-integration">
                                <Globe className="h-4 w-4" />
                                ATS Integration
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                </div>

                {/* Active Users Table */}
                {activeTab === 'active' && (
                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>User</TableHead>
                                        <TableHead>Dept / Designation</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Features</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar>
                                                        <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white">
                                                            {user.name?.charAt(0).toUpperCase()}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium">{user.name}</p>
                                                        <p className="text-sm text-muted-foreground">{user.email}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-1">
                                                    {user.department && (
                                                        <div className="flex items-center gap-1 text-sm">
                                                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                            <span className="font-medium">{user.department}</span>
                                                        </div>
                                                    )}
                                                    {user.designation && (
                                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                            <Briefcase className="h-3 w-3" />
                                                            {user.designation}
                                                        </div>
                                                    )}
                                                    {!user.department && !user.designation && (
                                                        <span className="text-muted-foreground text-sm">-</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={`${getRoleColor(user.role)} flex items-center gap-1 w-fit`}>
                                                    {getRoleIcon(user.role)}
                                                    {user.role}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 flex-wrap">
                                                    {user.feature_access?.staffing_training !== false && (
                                                        <span title="Staffing Training" className="p-1 rounded bg-cyan-500/10">
                                                            <GraduationCap className="h-3 w-3 text-cyan-500" />
                                                        </span>
                                                    )}
                                                    {user.feature_access?.staffing_training !== false && user.feature_access?.eeoc_training !== false && (
                                                        <span title="EEOC Training" className="p-1 rounded bg-sky-500/10" data-testid={`user-eeoc-access-${user.id}`}>
                                                            <Scale className="h-3 w-3 text-sky-500" />
                                                        </span>
                                                    )}
                                                    {user.feature_access?.quality_admin === true && (
                                                        <span title="Quality Admin" className="p-1 rounded bg-violet-500/10" data-testid={`user-quality-admin-${user.id}`}>
                                                            <ShieldCheck className="h-3 w-3 text-violet-500" />
                                                        </span>
                                                    )}
                                                    {user.feature_access?.inside_sales_training !== false && (
                                                        <span title="Inside Sales Training" className="p-1 rounded bg-indigo-500/10">
                                                            <Briefcase className="h-3 w-3 text-indigo-500" />
                                                        </span>
                                                    )}
                                                    {user.feature_access?.simulations !== false && (
                                                        <span title="Simulations" className="p-1 rounded bg-blue-500/10">
                                                            <Mic className="h-3 w-3 text-blue-500" />
                                                        </span>
                                                    )}
                                                    {user.feature_access?.evolve !== false && (
                                                        <span title="EVOLVE" className="p-1 rounded bg-orange-500/10">
                                                            <Video className="h-3 w-3 text-orange-500" />
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {user.is_locked ? (
                                                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                                                        <Lock className="h-3 w-3 mr-1" />
                                                        Locked
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50">
                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                        Active
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => openEdit(user)} data-testid={`action-edit-${user.id}`}>
                                                            <Edit className="h-4 w-4 mr-2" />
                                                            Edit User
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => openFeatureAccessDialog(user)} data-testid={`action-feature-access-${user.id}`}>
                                                            <Settings2 className="h-4 w-4 mr-2" />
                                                            Feature Access
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleLockToggle(user.id, user.is_locked)}
                                                            data-testid={`action-lock-${user.id}`}
                                                        >
                                                            {user.is_locked ? (
                                                                <>
                                                                    <Unlock className="h-4 w-4 mr-2" />
                                                                    Unlock User
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Lock className="h-4 w-4 mr-2" />
                                                                    Lock User
                                                                </>
                                                            )}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem 
                                                            onClick={() => openDeleteDialog(user.id)}
                                                            className="text-destructive focus:text-destructive"
                                                            data-testid={`action-delete-${user.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4 mr-2" />
                                                            Delete User
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredUsers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                {searchQuery ? 'No users match your search' : 'No users found'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}

                {/* Deleted Users Table */}
                {activeTab === 'deleted' && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Archive className="h-5 w-5 text-muted-foreground" />
                                Deleted Users
                            </CardTitle>
                            <CardDescription>
                                Users can be restored within 30 days of deletion. After that, only permanent deletion is available.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>User</TableHead>
                                        <TableHead>Deleted On</TableHead>
                                        <TableHead>Recovery Window</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredDeletedUsers.map((user) => {
                                        const daysLeft = getDaysUntilExpiry(user.deleted_at);
                                        return (
                                            <TableRow key={user.id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <Avatar className="opacity-50">
                                                            <AvatarFallback className="bg-muted text-muted-foreground">
                                                                {user.name?.charAt(0).toUpperCase()}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="opacity-70">
                                                            <p className="font-medium">{user.name}</p>
                                                            <p className="text-sm text-muted-foreground">{user.email}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground">
                                                        {user.deleted_at 
                                                            ? new Date(user.deleted_at).toLocaleDateString('en-US', {
                                                                month: 'short', day: 'numeric', year: 'numeric'
                                                            })
                                                            : '-'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Clock className={`h-4 w-4 ${daysLeft <= 7 ? 'text-red-500' : 'text-amber-500'}`} />
                                                        <span className={`text-sm ${daysLeft <= 7 ? 'text-red-500 font-medium' : ''}`}>
                                                            {daysLeft} days left
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleRestore(user.id)}
                                                            disabled={daysLeft === 0}
                                                            data-testid={`restore-user-${user.id}`}
                                                        >
                                                            <RotateCcw className="h-4 w-4 mr-2" />
                                                            Restore
                                                        </Button>
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => openPermanentDeleteDialog(user.id)}
                                                            data-testid={`permanent-delete-${user.id}`}
                                                        >
                                                            <XCircle className="h-4 w-4 mr-2" />
                                                            Delete Forever
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredDeletedUsers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                                <Archive className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
                                                No deleted users
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}

                {/* ATS Integration Tab */}
                {activeTab === 'ats' && (
                    <ATSIntegration api={api} />
                )}

                {/* Soft Delete Confirmation Dialog */}
                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                                Delete User?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                This user will be moved to the deleted users list. They will not be able to log in, 
                                but their data will be preserved for 30 days. You can restore them at any time during this period.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={handleSoftDelete}
                                className="bg-amber-600 hover:bg-amber-700"
                            >
                                {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Delete User
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Permanent Delete Confirmation Dialog */}
                <AlertDialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                                <XCircle className="h-5 w-5" />
                                Permanently Delete User?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                This action <strong>cannot be undone</strong>. The user and all their associated data 
                                (simulations, learning sessions, progress) will be permanently removed from the system.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={handlePermanentDelete}
                                className="bg-destructive hover:bg-destructive/90"
                            >
                                {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Delete Forever
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Feature Access Dialog */}
                <Dialog open={featureDialogOpen} onOpenChange={setFeatureDialogOpen}>
                    <DialogContent className="max-w-md" data-testid="feature-access-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Settings2 className="h-5 w-5 text-primary" />
                                Feature Access
                            </DialogTitle>
                            <DialogDescription>
                                Control which features {featureEditUser?.name} can access. Disabled features will not appear on their dashboard.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                            {/* Administration Access Section */}
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Administration Access</p>
                                <div className="flex items-center justify-between rounded-lg border border-l-2 border-l-violet-500 p-3" data-testid="feature-row-quality-admin">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-violet-500/10">
                                            <ShieldCheck className="h-4 w-4 text-violet-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Quality Admin</p>
                                            <p className="text-xs text-muted-foreground">
                                                {featureEditUser?.role === 'admin'
                                                    ? 'Master admins always have Quality Admin access'
                                                    : 'Assign simulations from call reviews, extend deadlines & pull reports'}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureEditUser?.role === 'admin' || featureAccess.quality_admin}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, quality_admin: checked })}
                                        disabled={featureEditUser?.role === 'admin'}
                                        data-testid="feature-toggle-quality-admin"
                                    />
                                </div>
                            </div>

                            {/* Training Programs Section */}
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Training Programs</p>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-cyan-500/10">
                                            <GraduationCap className="h-4 w-4 text-cyan-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Staffing Training</p>
                                            <p className="text-xs text-muted-foreground">R-PAT modules, simulations & sourcing</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.staffing_training}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, staffing_training: checked })}
                                        data-testid="feature-toggle-staffing-training"
                                    />
                                </div>
                                
                                <div className={`ml-5 flex items-center justify-between rounded-lg border border-l-2 border-l-sky-400 p-3 transition-opacity ${featureAccess.staffing_training ? '' : 'opacity-60'}`} data-testid="feature-row-eeoc-training">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-sky-500/10">
                                            <Scale className="h-4 w-4 text-sky-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">EEOC Training</p>
                                            <p className="text-xs text-muted-foreground">
                                                {featureAccess.staffing_training
                                                    ? 'Inside Staffing Training · Training & Certifications'
                                                    : 'Requires Staffing Training to be on'}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.eeoc_training}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, eeoc_training: checked })}
                                        disabled={!featureAccess.staffing_training}
                                        data-testid="feature-toggle-eeoc-training"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-indigo-500/10">
                                            <Briefcase className="h-4 w-4 text-indigo-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Inside Sales Training</p>
                                            <p className="text-xs text-muted-foreground">Sales skills & voice coaching</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.inside_sales_training}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, inside_sales_training: checked })}
                                        data-testid="feature-toggle-inside-sales"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-violet-500/10">
                                            <Mic className="h-4 w-4 text-violet-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Communication Training</p>
                                            <p className="text-xs text-muted-foreground">Communication & presentation skills</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.communication_training}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, communication_training: checked })}
                                        data-testid="feature-toggle-communication"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-amber-500/10">
                                            <Shield className="h-4 w-4 text-amber-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Leadership Training</p>
                                            <p className="text-xs text-muted-foreground">Leadership & management skills</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.leadership_training}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, leadership_training: checked })}
                                        data-testid="feature-toggle-leadership"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-cyan-500/10">
                                            <BookOpen className="h-4 w-4 text-cyan-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Tech Learning</p>
                                            <p className="text-xs text-muted-foreground">Tech courses & certifications</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.tech_learning}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, tech_learning: checked })}
                                        data-testid="feature-toggle-tech-learning"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-emerald-500/10">
                                            <DollarSign className="h-4 w-4 text-emerald-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Finance Training</p>
                                            <p className="text-xs text-muted-foreground">Finance courses & certifications</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.finance_training}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, finance_training: checked })}
                                        data-testid="feature-toggle-finance-training"
                                    />
                                </div>
                                
                                {/* Dynamic Course Modules */}
                                {dynamicModules.filter(m => m.is_active).map(module => {
                                    const featureKey = module.slug.replace(/-/g, '_');
                                    const colorMap = {
                                        blue: 'bg-blue-500/10 text-blue-500',
                                        green: 'bg-green-500/10 text-green-500',
                                        purple: 'bg-purple-500/10 text-purple-500',
                                        orange: 'bg-orange-500/10 text-orange-500',
                                        red: 'bg-red-500/10 text-red-500',
                                        cyan: 'bg-cyan-500/10 text-cyan-500',
                                        emerald: 'bg-emerald-500/10 text-emerald-500',
                                        amber: 'bg-amber-500/10 text-amber-500',
                                        pink: 'bg-pink-500/10 text-pink-500',
                                        indigo: 'bg-indigo-500/10 text-indigo-500'
                                    };
                                    const colorClass = colorMap[module.color] || colorMap.blue;
                                    return (
                                        <div key={module.id} className="flex items-center justify-between p-3 rounded-lg border">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${colorClass.split(' ')[0]}`}>
                                                    <BookOpen className={`h-4 w-4 ${colorClass.split(' ')[1]}`} />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{module.name}</p>
                                                    <p className="text-xs text-muted-foreground">{module.description || `${module.name} courses`}</p>
                                                </div>
                                            </div>
                                            <Switch
                                                checked={featureAccess[featureKey] !== false}
                                                onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, [featureKey]: checked })}
                                                data-testid={`feature-toggle-${featureKey}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {/* Features Section */}
                            <div className="space-y-2 pt-2 border-t">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Individual Features</p>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-blue-500/10">
                                            <Mic className="h-4 w-4 text-blue-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Simulations</p>
                                            <p className="text-xs text-muted-foreground">Communication practice scenarios</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.simulations}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, simulations: checked })}
                                        data-testid="feature-toggle-simulations"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-purple-500/10">
                                            <Briefcase className="h-4 w-4 text-purple-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Job Simulations</p>
                                            <p className="text-xs text-muted-foreground">Job-specific skill training</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.job_simulations}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, job_simulations: checked })}
                                        data-testid="feature-toggle-job-simulations"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-emerald-500/10">
                                            <Search className="h-4 w-4 text-emerald-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Sourcing Labs</p>
                                            <p className="text-xs text-muted-foreground">Candidate sourcing practice</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.sourcing_labs}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, sourcing_labs: checked })}
                                        data-testid="feature-toggle-sourcing-labs"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-orange-500/10">
                                            <Video className="h-4 w-4 text-orange-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">EVOLVE</p>
                                            <p className="text-xs text-muted-foreground">Presentation coaching</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={featureAccess.evolve}
                                        onCheckedChange={(checked) => setFeatureAccess({ ...featureAccess, evolve: checked })}
                                        data-testid="feature-toggle-evolve"
                                    />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setFeatureDialogOpen(false)} data-testid="feature-access-cancel-btn">
                                Cancel
                            </Button>
                            <Button onClick={handleFeatureAccessUpdate} disabled={actionLoading} data-testid="feature-access-save-btn">
                                {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Save Changes
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default Users;
